// frontend/src/pages/Home.jsx - COMPLETELY CLEANED
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faTruck,
  faCow,
  faLeaf,faSun,
  faRecycle,
  faGem,
  faTag,
  faShippingFast,
  faHeadset,
  faStarHalfAlt,
  faUsers,
  faGlobeAfrica,
  faClock,
  faCheckCircle,
  faPlay,
  faPause,
  faVolumeMute,
  faVolumeUp,
  faQuoteLeft,
  faTractor,
  faStore,
  faSeedling,
  faSpinner,
  faMobileAlt,
  faMoneyBillWave,
  faShieldAlt,
  faCertificate,
  faClipboardCheck,
  faHandshake,
  faCalendarCheck,
  faMoneyBillTransfer,
  faCreditCard,
  faStar as faStarSolid,
  faChevronRight,
  faPercent,
  faGift,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import { getProducts, getApprovedTestimonials } from "../api";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { useAuth } from "../context/AuthContext";
import heroVideo from "../assets/0523.mp4";
import heroVideoPoster from "../assets/0523-poster.jpg";
import ProductCard from "../components/Product/ProductCard";
import SEO from "../components/SEO/SEO";

// ============================================
// CONSTANTS
// ============================================
const STAGGER_DELAY = 0.1;
const FEATURED_PRODUCTS_LIMIT = 6;
const TESTIMONIALS_LIMIT = 4;

const BRAND = {
  primary: "#006B2E",
  primaryRgb: "0, 107, 46",
  secondary: "#D9A321",
  secondaryRgb: "217, 163, 33",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  lightBg: "#F8F8F8",
  white: "#FFFFFF",
  goldHover: "#B8860B",
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <>
      {[...Array(fullStars)].map((_, i) => (
        <FontAwesomeIcon
          key={`full-${i}`}
          icon={faStarSolid}
          style={{ color: BRAND.secondary }}
        />
      ))}
      {hasHalfStar && (
        <FontAwesomeIcon
          key="half"
          icon={faStarHalfAlt}
          style={{ color: BRAND.secondary }}
        />
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <FontAwesomeIcon
          key={`empty-${i}`}
          icon={faStarSolid}
          style={{ color: BRAND.secondary, opacity: 0.3 }}
        />
      ))}
    </>
  );
};

// ============================================
// HERO SECTION
// ============================================
const HeroSection = ({
  videoPlaying,
  videoMuted,
  videoRef,
  videoLoaded,
  onVideoLoaded,
  toggleVideoPlay,
  toggleVideoMute,
}) => {
  const { user } = useAuth();
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);
  const heroScale = useTransform(scrollY, [0, 500], [1, 0.95]);

  return (
    <motion.section
      className="hero-section"
      style={{ opacity: heroOpacity, scale: heroScale }}
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        poster={heroVideoPoster}
        onLoadedData={() => onVideoLoaded(true)}
        className="hero-video"
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      <div className="hero-poster" style={{ opacity: videoLoaded ? 0 : 1 }} />
      <div className="hero-overlay">
        <div className="hero-overlay-gradient" />
        <div className="hero-overlay-pattern" />
      </div>

      {/* Floating Elements */}
      <div className="hero-floating-elements">
        {[faSeedling, faCow, faTractor, faSun].map((icon, i) => (
          <motion.div
            key={i}
            className={`float-element float-${i + 1}`}
            animate={{ y: [0, -20 + i * 5, 0] }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.5,
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </motion.div>
        ))}
      </div>

      {/* Controls */}
      <div className="hero-controls">
        <motion.button
          onClick={toggleVideoPlay}
          aria-label={videoPlaying ? "Pause video" : "Play video"}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FontAwesomeIcon icon={videoPlaying ? faPause : faPlay} />
        </motion.button>
        <motion.button
          onClick={toggleVideoMute}
          aria-label={videoMuted ? "Unmute video" : "Mute video"}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FontAwesomeIcon icon={videoMuted ? faVolumeMute : faVolumeUp} />
        </motion.button>
      </div>

      {/* Content */}
      <div className="hero-content">
        <div className="hero-text">
          <motion.span
            className="hero-badge"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <FontAwesomeIcon icon={faSeedling} /> Kenali Enterprise
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Premium Livestock <br />
            <span className="highlight">Nutrition Solutions</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            Trusted by farmers across Sierra Leone. Premium animal feed
            supplements sourced from trusted global partners, delivered with
            real-time tracking.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <Button
              href="/products"
              variant="primary"
              size="large"
              icon={<FontAwesomeIcon icon={faArrowRight} />}
              iconPosition="right"
            >
              Shop Now
            </Button>
            <Button
              href="/preorder"
              variant="secondary"
              size="large"
              icon={<FontAwesomeIcon icon={faCalendarCheck} />}
            >
              Pre-Order
            </Button>
            <Button
              href={user ? "/track-order" : "/login"}
              variant="outline"
              size="large"
            >
              {user ? "Track Order" : "Login"}
            </Button>
          </motion.div>

          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <div className="hero-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span>500+ Happy Farmers</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faGlobeAfrica} />
              <span>Global Sourcing</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faClock} />
              <span>24/7 Support</span>
            </div>
          </motion.div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          className="hero-trust-indicators"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="trust-indicator">
            <FontAwesomeIcon icon={faCertificate} />
            <span>NRA Registered</span>
          </div>
          <div className="trust-indicator">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Quality Assured</span>
          </div>
          <div className="trust-indicator">
            <FontAwesomeIcon icon={faHandshake} />
            <span>Trusted Partner</span>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// ============================================
// PRE-ORDER PROMO SECTION
// ============================================
const PreOrderPromoSection = () => {
  return (
    <section className="preorder-promo">
      <div className="preorder-pattern" />
      <div className="container">
        <motion.div
          className="preorder-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <motion.div
            className="preorder-badge"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <FontAwesomeIcon icon={faRocket} /> Limited Pre-Order
          </motion.div>

          <div className="preorder-icon">🌾</div>
          <h2>Pre-Order Kenali GrowPro™</h2>
          <p>
            Reserve your 25kg or 50kg pack before the public release.
            <br />
            <strong className="highlight">
              50% deposit now, 50% balance on pickup.
            </strong>
          </p>

          <div className="preorder-features">
            <div className="preorder-feature">
              <FontAwesomeIcon icon={faGift} />
              <span>Priority Access</span>
            </div>
            <div className="preorder-feature">
              <FontAwesomeIcon icon={faPercent} />
              <span>10% Pre-Order Discount</span>
            </div>
            <div className="preorder-feature">
              <FontAwesomeIcon icon={faShippingFast} />
              <span>First Batch Delivery</span>
            </div>
          </div>

          <div className="preorder-buttons">
            <Button
              href="/preorder"
              variant="primary"
              size="large"
              icon={<FontAwesomeIcon icon={faCalendarCheck} />}
            >
              Pre-Order Now
            </Button>
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Order
            </a>
          </div>
          <div className="preorder-note">
            Limited stock available • Priority access for pre-order customers
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// TRUST SECTION
// ============================================
const TrustSection = () => {
  const trustBadges = [
    {
      icon: faCertificate,
      label: "NRA Registered",
      detail: "No: 1001577205",
      color: BRAND.secondary,
    },
    {
      icon: faShieldAlt,
      label: "Quality Guaranteed",
      detail: "COA Available",
      color: "#10b981",
    },
    {
      icon: faClipboardCheck,
      label: "Batch Tracked",
      detail: "Every order traceable",
      color: "#3b82f6",
    },
    {
      icon: faHandshake,
      label: "Trusted Partner",
      detail: "Since 2024",
      color: "#8b5cf6",
    },
  ];

  return (
    <section className="trust-section">
      <div className="container">
        <motion.div
          className="trust-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="section-tag">Trusted & Verified</span>
          <h2>Your Trusted Partner in Livestock Nutrition</h2>
          <p>
            Kenali Enterprise is fully registered and compliant with Sierra
            Leone regulations
          </p>
        </motion.div>

        <div className="trust-grid">
          {trustBadges.map((badge, index) => (
            <motion.div
              key={index}
              className="trust-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
            >
              <div
                className="trust-icon"
                style={{ background: `${badge.color}15` }}
              >
                <FontAwesomeIcon
                  icon={badge.icon}
                  style={{ color: badge.color }}
                />
              </div>
              <h4>{badge.label}</h4>
              <p>{badge.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FEATURES SECTION
// ============================================
const FeaturesSection = () => {
  const features = useMemo(
    () => [
      {
        icon: faGem,
        title: "Premium Quality",
        desc: "Sourced from top global manufacturers with full traceability",
        color: BRAND.secondary,
      },
      {
        icon: faTag,
        title: "Best Prices",
        desc: "Competitive pricing with bulk discounts for farmers",
        color: "#10b981",
      },
      {
        icon: faShippingFast,
        title: "Fast Delivery",
        desc: "Real-time tracking from port to farm in Sierra Leone",
        color: "#3b82f6",
      },
      {
        icon: faHeadset,
        title: "Expert Support",
        desc: "Technical guidance via WhatsApp for optimal results",
        color: "#8b5cf6",
      },
      {
        icon: faMobileAlt,
        title: "Mobile Money",
        desc: "Pay with Orange Money, Afrimoney & WAVE",
        color: "#f59e0b",
      },
      {
        icon: faTractor,
        title: "Farmer Focused",
        desc: "Bulk and small-pack options for every farm size",
        color: BRAND.primary,
      },
      {
        icon: faCalendarCheck,
        title: "Pre-Order Available",
        desc: "Reserve your GrowPro with 50% deposit",
        color: BRAND.secondary,
      },
      {
        icon: faGlobeAfrica,
        title: "Global Sourcing",
        desc: "Premium quality products sourced from trusted global partners",
        color: "#8b5cf6",
      },
    ],
    [],
  );

  return (
    <section className="features-section">
      <div className="container">
        <div className="features-header">
          <span className="section-tag">Why Kenali Enterprise</span>
          <h2>Designed for Sierra Leone Farmers</h2>
          <p>
            We understand the unique challenges of livestock farming in Sierra
            Leone
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
            >
              <div
                className="feature-icon"
                style={{ background: `${feature.color}15` }}
              >
                <FontAwesomeIcon
                  icon={feature.icon}
                  style={{ color: feature.color }}
                />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// PAYMENT METHODS SECTION
// ============================================
const PaymentMethodsSection = () => {
  const paymentMethods = [
    { icon: faMoneyBillWave, label: "Cash on Delivery", color: "#10b981" },
    { icon: faMobileAlt, label: "Orange Money", color: "#f59e0b" },
    { icon: faMobileAlt, label: "Afrimoney", color: "#ef4444" },
    { icon: faMobileAlt, label: "WAVE", color: "#3b82f6" },
    { icon: faMoneyBillTransfer, label: "Bank Transfer", color: "#8b5cf6" },
    { icon: faCreditCard, label: "Card Payment", color: "#6b7280" },
  ];

  return (
    <section className="payments-section">
      <div className="container">
        <div className="payments-header">
          <span className="section-tag">Flexible Payments</span>
          <h3>Multiple Payment Options for Sierra Leone</h3>
          <p>Pay the way that works best for you</p>
        </div>

        <div className="payments-grid">
          {paymentMethods.map((method, index) => (
            <motion.div
              key={index}
              className="payment-method"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <FontAwesomeIcon
                icon={method.icon}
                style={{ color: method.color }}
              />
              <span>{method.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CATEGORIES SECTION
// ============================================
const CategoriesSection = () => {
  const categories = useMemo(
    () => [
      {
        name: "Rumen Bypass Fat",
        icon: faCow,
        slug: "rumen-bypass-fat",
        color: BRAND.primary,
        description: "Boost milk production & energy",
      },
      {
        name: "Mineral Blocks",
        icon: faGem,
        slug: "mineral-blocks",
        color: BRAND.secondary,
        description: "Essential minerals & salts",
      },
      {
        name: "Protein Concentrates",
        icon: faLeaf,
        slug: "protein-concentrates",
        color: "#16a34a",
        description: "High protein for growth",
      },
      {
        name: "Feed Supplements",
        icon: faRecycle,
        slug: "feed-supplements",
        color: "#8b5cf6",
        description: "Complete nutrition mix",
      },
    ],
    [],
  );

  return (
    <section className="categories-section">
      <div className="container">
        <div className="categories-header">
          <span className="section-tag">Shop by Category</span>
          <h2>Find the Right Product for Your Livestock</h2>
          <p>Browse our range of premium animal feed supplements</p>
        </div>

        <div className="categories-grid">
          {categories.map((category, index) => (
            <motion.div
              key={index}
              className="category-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
            >
              <Link
                to={`/products?category=${category.slug}`}
                className="category-link"
              >
                <div
                  className="category-icon"
                  style={{ background: `${category.color}15` }}
                >
                  <FontAwesomeIcon
                    icon={category.icon}
                    style={{ color: category.color }}
                  />
                </div>
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                <span className="shop-link">
                  Shop Now <FontAwesomeIcon icon={faChevronRight} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FEATURED PRODUCTS SECTION
// ============================================
const FeaturedProductsSection = ({ products, loading }) => {
  const { user } = useAuth();
  const userType = user?.userType || "customer";
  const farmSize = user?.farmDetails?.farmSize || "small";

  return (
    <section className="featured-section">
      <div className="container">
        <div className="featured-header">
          <span className="section-tag">Best Sellers</span>
          <h2>Featured Products</h2>
          <p>
            Our most popular products trusted by farmers across Sierra Leone
          </p>
        </div>

        {loading ? (
          <div className="loader-container">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="3x"
              style={{ color: BRAND.primary }}
            />
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.length > 0 ? (
                products
                  .slice(0, FEATURED_PRODUCTS_LIMIT)
                  .map((product, index) => (
                    <motion.div
                      key={product._id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * STAGGER_DELAY }}
                      viewport={{ once: true }}
                    >
                      <ProductCard
                        product={product}
                        userType={userType}
                        farmSize={farmSize}
                      />
                    </motion.div>
                  ))
              ) : (
                <div className="empty-products">
                  <p>No featured products available at the moment.</p>
                  <Button href="/products" variant="primary">
                    View All Products
                  </Button>
                </div>
              )}
            </div>
            {products.length > 0 && (
              <div className="view-all">
                <Button
                  href="/products"
                  variant="primary"
                  icon={<FontAwesomeIcon icon={faArrowRight} />}
                  iconPosition="right"
                >
                  View All Products
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

// ============================================
// TESTIMONIALS SECTION
// ============================================
const TestimonialsSection = ({ testimonials, stats }) => {
  return (
    <section className="testimonials-section">
      <div className="container">
        <div className="testimonials-header">
          <span className="section-tag">Testimonials</span>
          <h2>What Our Customers Say</h2>
          <p>
            {stats.total || testimonials.length}+ satisfied farmers and
            customers
            {stats.averageRating &&
              ` • ${stats.averageRating.toFixed(1)} / 5 average rating`}
          </p>
        </div>

        <div className="testimonials-grid">
          {testimonials.length > 0 ? (
            testimonials
              .slice(0, TESTIMONIALS_LIMIT)
              .map((testimonial, index) => (
                <motion.div
                  key={testimonial._id}
                  className="testimonial-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * STAGGER_DELAY }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6 }}
                >
                  <div className="testimonial-quote">
                    <FontAwesomeIcon icon={faQuoteLeft} />
                  </div>
                  <div className="testimonial-stars">
                    {renderStars(testimonial.rating)}
                  </div>
                  <h3>{testimonial.title}</h3>
                  <p>
                    "
                    {testimonial.content.length > 200
                      ? testimonial.content.substring(0, 200) + "..."
                      : testimonial.content}
                    "
                  </p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      {testimonial.avatar ? (
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.userName}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={
                            testimonial.userType === "farmer"
                              ? faTractor
                              : faStore
                          }
                        />
                      )}
                    </div>
                    <div>
                      <strong>{testimonial.userName}</strong>
                      <span>
                        {testimonial.userType === "farmer"
                          ? "Farmer"
                          : "Customer"}
                      </span>
                    </div>
                  </div>
                  {testimonial.productName && (
                    <div className="testimonial-product">
                      Product: {testimonial.productName}
                    </div>
                  )}
                </motion.div>
              ))
          ) : (
            <div className="empty-testimonials">
              <p>
                No testimonials available yet. Be the first to share your
                experience!
              </p>
              <Button href="/testimonials" variant="outline">
                Share Your Experience
              </Button>
            </div>
          )}
        </div>

        {testimonials.length > 0 && (
          <div className="view-all-testimonials">
            <Button href="/testimonials" variant="outline">
              Read More Testimonials
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================
// ABOUT SECTION
// ============================================
const AboutSection = () => {
  const features = [
    "NRA Registered: 1001577205",
    "Quality tested with COA available",
    "Bulk & small-pack options",
    "Real-time shipment tracking",
    "WhatsApp support for farmers",
    "Mobile money payments accepted",
    "Pre-orders available with 50% deposit",
    "Sourced from trusted global partners",
  ];

  return (
    <section className="about-section">
      <div className="container">
        <div className="about-grid">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-tag">About Kenali Enterprise</span>
            <h2>Trusted Partner for Farmers Across Sierra Leone</h2>
            <p>
              Kenali Enterprise is dedicated to providing premium animal feed
              supplements to farmers across Sierra Leone. We source our products
              from trusted global manufacturers, ensuring the highest quality at
              competitive prices.
            </p>
            <div className="about-features">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  className="about-feature"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    style={{ color: "#16a34a" }}
                  />
                  <span>{feature}</span>
                </motion.div>
              ))}
            </div>
            <div className="about-buttons">
              <Button href="/about" variant="primary">
                Learn More
              </Button>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-btn"
              >
                <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
              </a>
            </div>
          </motion.div>

          <motion.div
            className="about-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="about-image-content">
              <div className="about-image-pattern" />
              <FontAwesomeIcon icon={faTruck} className="about-icon" />
              <div className="about-brand">Kenali Enterprise</div>
              <div className="about-tagline">Premium Livestock Nutrition</div>
              <div className="about-nra">NRA: 1001577205</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// STATS SECTION
// ============================================
const StatsSection = ({ testimonialStats }) => {
  const stats = useMemo(
    () => [
      {
        value: testimonialStats.total || "500+",
        label: "Happy Farmers",
        icon: faUsers,
        color: BRAND.primary,
      },
      {
        value: "50+",
        label: "Tons Delivered",
        icon: faTruck,
        color: "#3b82f6",
      },
      {
        value: testimonialStats.averageRating
          ? `${testimonialStats.averageRating.toFixed(1)} / 5`
          : "4.8 / 5",
        label: "Customer Rating",
        icon: faStarSolid,
        color: BRAND.secondary,
      },
      {
        value: "24/7",
        label: "WhatsApp Support",
        icon: faWhatsappBrand,
        color: "#25d366",
      },
      {
        value: "Global Sourcing",
        label: "Product Origin",
        icon: faGlobeAfrica,
        color: "#8b5cf6",
      },
    ],
    [testimonialStats],
  );

  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    stats.forEach((stat, index) => {
      const numericValue = parseInt(String(stat.value).replace(/[^0-9]/g, ""));
      if (!isNaN(numericValue)) {
        let start = 0;
        const increment = Math.ceil(numericValue / 60);
        const timer = setInterval(() => {
          start += increment;
          if (start >= numericValue) {
            start = numericValue;
            clearInterval(timer);
          }
          setCounts((prev) => {
            const newCounts = [...prev];
            newCounts[index] = start;
            return newCounts;
          });
        }, 30);
        return () => clearInterval(timer);
      }
    });
  }, []);

  return (
    <section className="stats-section">
      <div className="container">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="stat-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{
                delay: index * STAGGER_DELAY,
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div
                className="stat-icon"
                style={{ background: `${stat.color}15` }}
              >
                <FontAwesomeIcon
                  icon={stat.icon}
                  style={{ color: stat.color }}
                />
              </div>
              <div className="stat-value">
                {typeof stat.value === "string" && isNaN(parseInt(stat.value))
                  ? stat.value
                  : counts[index] || stat.value}
              </div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CTA SECTION
// ============================================
const CTASection = () => {
  return (
    <section className="cta-section">
      <div className="cta-pattern" />
      <div className="container">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="cta-badge"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <FontAwesomeIcon icon={faRocket} /> Start Today
          </motion.div>

          <h2>Ready to Improve Your Livestock Nutrition?</h2>
          <p>
            Contact us for bulk orders or expert advice on animal nutrition.
          </p>

          <div className="cta-buttons">
            <Button
              href="/products"
              variant="primary"
              size="large"
              icon={<FontAwesomeIcon icon={faArrowRight} />}
              iconPosition="right"
            >
              Shop Now
            </Button>
            <Button href="/contact" variant="outline" size="large">
              Contact Sales
            </Button>
            <Button
              href="/preorder"
              variant="secondary"
              size="large"
              icon={<FontAwesomeIcon icon={faCalendarCheck} />}
            >
              Pre-Order
            </Button>
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// MAIN HOME COMPONENT
// ============================================
const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialStats, setTestimonialStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, testimonialsRes] = await Promise.all([
        getProducts({ featured: true, limit: FEATURED_PRODUCTS_LIMIT }),
        getApprovedTestimonials({ limit: TESTIMONIALS_LIMIT, featured: true }),
      ]);

      setFeaturedProducts(productsRes.data?.products || []);
      setTestimonials(testimonialsRes.data?.testimonials || []);
      setTestimonialStats(testimonialsRes.data?.stats || {});
    } catch (error) {
      console.error("Error fetching home data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVideoLoaded = useCallback((loaded) => {
    setVideoLoaded(loaded);
  }, []);

  const toggleVideoPlay = useCallback(() => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
        setVideoPlaying(false);
      } else {
        videoRef.current.play();
        setVideoPlaying(true);
      }
    }
  }, [videoPlaying]);

  const toggleVideoMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoMuted;
      setVideoMuted(!videoMuted);
    }
  }, [videoMuted]);

  if (loading) return <Loader />;

  return (
    <>
      <SEO
        title="Home - Premium Livestock Feed & Animal Nutrition Solutions"
        description="Kenali Enterprise provides premium livestock feed supplements, animal nutrition products, and farming solutions in Sierra Leone. Shop now for quality animal feed."
        keywords="livestock feed Sierra Leone, animal nutrition supplements, cattle feed, poultry feed, goat feed, sheep feed, farming solutions, Kenali Enterprise"
        canonical="https://kenalienterprise.com/"
      />
      <div className="home-page">
        <HeroSection
          videoPlaying={videoPlaying}
          videoMuted={videoMuted}
          videoRef={videoRef}
          videoLoaded={videoLoaded}
          onVideoLoaded={handleVideoLoaded}
          toggleVideoPlay={toggleVideoPlay}
          toggleVideoMute={toggleVideoMute}
        />
        <PreOrderPromoSection />
        <TrustSection />
        <FeaturesSection />
        <PaymentMethodsSection />
        <CategoriesSection />
        <FeaturedProductsSection
          products={featuredProducts}
          loading={loading}
        />
        <TestimonialsSection
          testimonials={testimonials}
          stats={testimonialStats}
        />
        <AboutSection />
        <StatsSection testimonialStats={testimonialStats} />
        <CTASection />
      </div>
    </>
  );
};

export default Home;
